import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  getAllEntries,
  updateEntryStatus,
  deleteEntry,
  openEntryPhoto,
  getEntryPhoto,
} from '../../../services/faceOfBelvoirService';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Wrapper = styled.div``;

const ToolBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 0.65rem 1rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.93rem;
  font-family: inherit;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FilterSelect = styled.select`
  padding: 0.65rem 1rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.93rem;
  font-family: inherit;
  background: white;
  cursor: pointer;

  &:focus { outline: none; }
`;

const RefreshButton = styled.button`
  padding: 0.65rem 1.25rem;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 8px;
  background: white;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const StatChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.9rem;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  background: ${props => {
    if (props.status === 'new')        return '#fff3cd';
    if (props.status === 'reviewing')  return '#d1ecf1';
    if (props.status === 'shortlisted') return '#d4edda';
    if (props.status === 'rejected')   return '#f8d7da';
    if (props.status === 'winner')     return '#e2d9f3';
    return '#e9ecef';
  }};
  color: ${props => {
    if (props.status === 'new')        return '#856404';
    if (props.status === 'reviewing')  return '#0c5460';
    if (props.status === 'shortlisted') return '#155724';
    if (props.status === 'rejected')   return '#721c24';
    if (props.status === 'winner')     return '#432874';
    return '#495057';
  }};
`;

const EntryCard = styled.div`
  background: white;
  border: 2px solid ${props => {
    if (props.status === 'winner')     return '#6f42c1';
    if (props.status === 'shortlisted') return '#28a745';
    if (props.status === 'rejected')    return '#dc3545';
    if (props.status === 'reviewing')   return '#17a2b8';
    return 'rgba(16,29,64,0.1)';
  }};
  border-radius: 14px;
  margin-bottom: 1.25rem;
  overflow: hidden;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 1rem;
  gap: 1rem;
  flex-wrap: wrap;
  cursor: pointer;
  user-select: none;
`;

const CardLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const EntryName = styled.h3`
  margin: 0 0 0.25rem;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 700;
`;

const EntryMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1rem;
  font-size: 0.87rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.75;
`;

const CardRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const StatusBadge = styled.span`
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${props => {
    if (props.status === 'new')        return '#fff3cd';
    if (props.status === 'reviewing')  return '#d1ecf1';
    if (props.status === 'shortlisted') return '#d4edda';
    if (props.status === 'rejected')   return '#f8d7da';
    if (props.status === 'winner')     return '#e2d9f3';
    return '#e9ecef';
  }};
  color: ${props => {
    if (props.status === 'new')        return '#856404';
    if (props.status === 'reviewing')  return '#0c5460';
    if (props.status === 'shortlisted') return '#155724';
    if (props.status === 'rejected')   return '#721c24';
    if (props.status === 'winner')     return '#432874';
    return '#495057';
  }};
`;

const ExpandToggle = styled.span`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.5;
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'none'};
  display: inline-block;
  transition: transform 0.2s ease;
`;

const CardBody = styled.div`
  display: ${props => props.expanded ? 'block' : 'none'};
  padding: 0 1.5rem 1.5rem;
  border-top: 1px solid rgba(16,29,64,0.08);
`;

const BodyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin: 1rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoBlock = styled.div`
  background: ${props => props.theme.colors.offWhite};
  border-radius: 10px;
  padding: 1rem;
`;

const InfoLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.theme.colors.primary};
  opacity: 0.55;
  margin-bottom: 0.4rem;
`;

const InfoValue = styled.div`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.primary};
  word-break: break-word;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const PhotoRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  align-items: center;
`;

const PhotoButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border-radius: 8px;
  border: 2px solid ${props => props.theme.colors.accent};
  background: white;
  color: ${props => props.theme.colors.accent};
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.accent};
    color: white;
    transform: translateY(-1px);
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const PhotoThumb = styled.img`
  max-width: 120px;
  max-height: 80px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid rgba(16,29,64,0.1);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
`;

const StatusPanel = styled.div`
  background: ${props => props.theme.colors.offWhite};
  border-radius: 10px;
  padding: 1rem;
  margin-top: 0.75rem;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
`;

const StatusSelect = styled.select`
  padding: 0.55rem 0.9rem;
  border: 2px solid rgba(16,29,64,0.15);
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: inherit;
  background: white;
  cursor: pointer;
  flex: 1;
  min-width: 160px;

  &:focus { outline: none; border-color: ${props => props.theme.colors.primary}; }
`;

const NotesInput = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 0.65rem 0.85rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: inherit;
  min-height: 70px;
  resize: vertical;
  margin-bottom: 0.75rem;

  &:focus { outline: none; border-color: ${props => props.theme.colors.primary}; }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const SaveButton = styled.button`
  padding: 0.55rem 1.1rem;
  border-radius: 8px;
  border: 2px solid ${props => props.theme.colors.primary};
  background: ${props => props.theme.colors.primary};
  color: white;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const DeleteButton = styled.button`
  padding: 0.55rem 1.1rem;
  border-radius: 8px;
  border: 2px solid #dc3545;
  background: white;
  color: #dc3545;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #dc3545;
    color: white;
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SaveFeedback = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.ok ? '#155724' : '#721c24'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  color: ${props => props.theme.colors.secondary};
  font-size: 0.95rem;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  color: ${props => props.theme.colors.secondary};
`;

const ErrorBanner = styled.div`
  padding: 1rem;
  border-radius: 8px;
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  font-size: 0.93rem;
  margin-bottom: 1.25rem;
`;

const MarketingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.78rem;
  font-weight: 700;
  background: ${props => props.yes ? '#d4edda' : '#f8d7da'};
  color: ${props => props.yes ? '#155724' : '#721c24'};
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dt) {
  if (!dt) return '—';
  const d = new Date(dt.replace(' ', 'T'));
  return d.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

function statusLabel(s) {
  return { new: 'New', reviewing: 'Reviewing', shortlisted: 'Shortlisted', rejected: 'Rejected', winner: 'Winner' }[s] || s;
}

// ---------------------------------------------------------------------------
// Entry row component
// ---------------------------------------------------------------------------

const EntryRow = ({ entry, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus]   = useState(entry.status);
  const [notes, setNotes]     = useState(entry.notes || '');
  const [saving, setSaving]   = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [photoSrc, setPhotoSrc] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);

  const loadPhoto = useCallback(async () => {
    if (photoSrc || !entry.photo_file_name) return;
    setPhotoLoading(true);
    try {
      const photo = await getEntryPhoto(entry.id);
      setPhotoSrc(`data:${photo.mimeType};base64,${photo.content}`);
    } catch {
      // silently fail — user can still click "Open Full Photo"
    } finally {
      setPhotoLoading(false);
    }
  }, [entry.id, entry.photo_file_name, photoSrc]);

  const handleExpand = () => {
    setExpanded(e => {
      if (!e) loadPhoto();
      return !e;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveFeedback(null);
    try {
      await updateEntryStatus(entry.id, status, notes);
      setSaveFeedback({ ok: true, text: 'Saved!' });
    } catch (err) {
      setSaveFeedback({ ok: false, text: err.message || 'Save failed.' });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveFeedback(null), 3000);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete entry from ${entry.name}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteEntry(entry.id);
      onDelete(entry.id);
    } catch (err) {
      alert(err.message || 'Failed to delete.');
      setDeleting(false);
    }
  };

  return (
    <EntryCard status={status}>
      <CardHeader onClick={handleExpand}>
        <CardLeft>
          <EntryName>{entry.name}</EntryName>
          <EntryMeta>
            <span>{entry.email}</span>
            {entry.phone && <span>{entry.phone}</span>}
            <span>{formatDate(entry.submitted_at)}</span>
            <MarketingBadge yes={entry.marketing_consent === '1' || entry.marketing_consent === 1}>
              {entry.marketing_consent === '1' || entry.marketing_consent === 1 ? 'Marketing: Yes' : 'Marketing: No'}
            </MarketingBadge>
          </EntryMeta>
        </CardLeft>
        <CardRight>
          <StatusBadge status={status}>{statusLabel(status)}</StatusBadge>
          <ExpandToggle expanded={expanded}>▼</ExpandToggle>
        </CardRight>
      </CardHeader>

      <CardBody expanded={expanded}>
        {/* Contact & family info */}
        <BodyGrid>
          <InfoBlock>
            <InfoLabel>Address</InfoLabel>
            <InfoValue>{entry.address || '—'}</InfoValue>
          </InfoBlock>
          <InfoBlock>
            <InfoLabel>Family Info</InfoLabel>
            <InfoValue>{entry.family_info || '—'}</InfoValue>
          </InfoBlock>
          <InfoBlock style={{ gridColumn: 'span 2' }}>
            <InfoLabel>Availability</InfoLabel>
            <InfoValue>{entry.availability || '—'}</InfoValue>
          </InfoBlock>
        </BodyGrid>

        {/* Photo */}
        {entry.photo_file_name && (
          <PhotoRow>
            {photoLoading && <span style={{ fontSize: '0.88rem', opacity: 0.6 }}>Loading photo…</span>}
            {photoSrc && (
              <PhotoThumb
                src={photoSrc}
                alt={`Family photo for ${entry.name}`}
                onClick={() => openEntryPhoto(entry.id)}
                title="Click to open full size"
              />
            )}
            <PhotoButton
              onClick={() => openEntryPhoto(entry.id)}
            >
              View Full Photo
            </PhotoButton>
            <span style={{ fontSize: '0.82rem', opacity: 0.6 }}>{entry.photo_file_name}</span>
          </PhotoRow>
        )}

        {/* Status & notes */}
        <StatusPanel>
          <InfoLabel style={{ marginBottom: '0.6rem' }}>Update Status</InfoLabel>
          <StatusRow>
            <StatusSelect value={status} onChange={e => setStatus(e.target.value)}>
              <option value="new">New</option>
              <option value="reviewing">Reviewing</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="rejected">Rejected</option>
              <option value="winner">Winner</option>
            </StatusSelect>
          </StatusRow>
          <NotesInput
            placeholder="Internal notes (not visible to entrant)…"
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
          <ActionRow>
            <SaveButton onClick={handleSave} disabled={saving || deleting}>
              {saving ? 'Saving…' : 'Save'}
            </SaveButton>
            <DeleteButton onClick={handleDelete} disabled={deleting || saving}>
              {deleting ? 'Deleting…' : 'Delete Entry'}
            </DeleteButton>
            {saveFeedback && (
              <SaveFeedback ok={saveFeedback.ok}>{saveFeedback.text}</SaveFeedback>
            )}
          </ActionRow>
        </StatusPanel>
      </CardBody>
    </EntryCard>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const FaceOfBelvoirModeration = () => {
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const data = await getAllEntries();
      setEntries(data);
    } catch (err) {
      setFetchError(err.message || 'Failed to load entries.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = useCallback(id => {
    setEntries(prev => prev.filter(e => e.id !== id));
  }, []);

  const filtered = entries.filter(e => {
    const matchesSearch =
      !search ||
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.email.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || e.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const counts = entries.reduce((acc, e) => {
    acc[e.status] = (acc[e.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <Wrapper>
      <ToolBar>
        <SearchInput
          type="text"
          placeholder="Search by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <FilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All statuses ({entries.length})</option>
          <option value="new">New ({counts.new || 0})</option>
          <option value="reviewing">Reviewing ({counts.reviewing || 0})</option>
          <option value="shortlisted">Shortlisted ({counts.shortlisted || 0})</option>
          <option value="rejected">Rejected ({counts.rejected || 0})</option>
          <option value="winner">Winner ({counts.winner || 0})</option>
        </FilterSelect>
        <RefreshButton onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </RefreshButton>
      </ToolBar>

      {!loading && entries.length > 0 && (
        <StatsRow>
          {['new', 'reviewing', 'shortlisted', 'rejected', 'winner'].map(s =>
            counts[s] ? (
              <StatChip key={s} status={s}>
                {statusLabel(s)} — {counts[s]}
              </StatChip>
            ) : null
          )}
        </StatsRow>
      )}

      {fetchError && <ErrorBanner>{fetchError}</ErrorBanner>}

      {loading ? (
        <LoadingState>Loading entries…</LoadingState>
      ) : filtered.length === 0 ? (
        <EmptyState>
          {entries.length === 0
            ? 'No entries have been received yet.'
            : 'No entries match your current filters.'}
        </EmptyState>
      ) : (
        filtered.map(entry => (
          <EntryRow key={entry.id} entry={entry} onDelete={handleDelete} />
        ))
      )}
    </Wrapper>
  );
};

export default FaceOfBelvoirModeration;
